type TreeQuery = { name: string, description: string, folder_name: string };

export type FolderNode<Q> = {
  /** Last level of the path, e.g. 'resolved' for 'supportcases/resolved'. '' for the root. */
  name: string,
  /** Full path, as stored in folder_name. '' for the root. */
  path: string,
  folders: FolderNode<Q>[],
  queries: Q[],
  /** Queries in this folder and every folder below it. */
  count: number,
};

/** Trim each level and drop empty ones. */
export function normalizeFolderPath(folder: string | null | undefined): string {
  return (folder ?? '').split('/').map(level => level.trim()).filter(Boolean).join('/');
}

/** 'a/b/c' -> ['a', 'a/b', 'a/b/c']. */
export function folderAndAncestors(path: string): string[] {
  const levels = normalizeFolderPath(path).split('/').filter(Boolean);
  return levels.map((_, i) => levels.slice(0, i + 1).join('/'));
}

/** Every folder that exists, including parents that only contain subfolders, sorted. */
export function allFolderPaths(queries: TreeQuery[]): string[] {
  const paths = new Set<string>();
  for (const q of queries) {
    let path = normalizeFolderPath(q.folder_name);
    // A path is only added with all of it's parent folders, so once it is in the set, every parent folder is aswell.
    while (path && !paths.has(path)) {
      paths.add(path);
      const slash = path.lastIndexOf('/');
      path = slash === -1 ? '' : path.slice(0, slash);
    }
  }
  return Array.from(paths).sort((a, b) => a.localeCompare(b));
}

export function matchesSearch(query: TreeQuery, term: string): boolean {
  const t = term.trim().toLowerCase();
  return !t
    || query.name.toLowerCase().includes(t)
    || query.description.toLowerCase().includes(t)
    || query.folder_name.toLowerCase().includes(t);
}

export function buildFolderTree<Q extends TreeQuery>(queries: Q[]): FolderNode<Q> {
  const root: FolderNode<Q> = { name: '', path: '', folders: [], queries: [], count: 0 };
  const byPath = new Map<string, FolderNode<Q>>([['', root]]);

  const nodeFor = (path: string): FolderNode<Q> => {
    const existing = byPath.get(path);
    if (existing) return existing;
    const slash = path.lastIndexOf('/');
    const parent = nodeFor(slash === -1 ? '' : path.slice(0, slash));
    const node: FolderNode<Q> = { name: path.slice(slash + 1), path, folders: [], queries: [], count: 0 };
    parent.folders.push(node);
    byPath.set(path, node);
    return node;
  };

  for (const q of queries) nodeFor(normalizeFolderPath(q.folder_name)).queries.push(q);

  const finish = (node: FolderNode<Q>): number => {
    node.folders.sort((a, b) => a.name.localeCompare(b.name));
    node.queries.sort((a, b) => a.name.localeCompare(b.name));
    node.count = node.queries.length + node.folders.reduce((sum, child) => sum + finish(child), 0);
    return node.count;
  };
  finish(root);
  return root;
}

/** After moving folder `from` to `to`, maps a path that was at or under `from` to its new location. */
export function movedPath(path: string, from: string, to: string): string {
  if (path === from) return to;
  return path.startsWith(from + '/') ? to + path.slice(from.length) : path;
}

export type FolderPathNode = { name: string, path: string, children: FolderPathNode[] };

/** Nests a flat, complete list of folder paths into a sorted tree of top-level folders. */
export function folderPathTree(paths: string[]): FolderPathNode[] {
  const roots: FolderPathNode[] = [];
  const byPath = new Map<string, FolderPathNode>();
  // Parents sort before their children, so each parent exists by the time its children are reached.
  for (const path of [...paths].sort((a, b) => a.localeCompare(b))) {
    const slash = path.lastIndexOf('/');
    const node = { name: path.slice(slash + 1), path, children: [] };
    byPath.set(path, node);
    const parent = slash === -1 ? undefined : byPath.get(path.slice(0, slash));
    (parent ? parent.children : roots).push(node);
  }
  return roots;
}

/**
 * Gives each level of a path the casing of an existing folder that differs only in case, so a new path is created
 * inside the existing folders: 'Support/new' becomes 'support/new' when 'support' exists. New levels keep their casing.
 */
export function matchFolderCase(folders: string[], path: string): string {
  const byLowerCase = new Map<string, string>();
  for (const f of folders) if (!byLowerCase.has(f.toLowerCase())) byLowerCase.set(f.toLowerCase(), f);
  let result = '';
  for (const level of path.split('/').filter(Boolean)) {
    const next = result ? `${result}/${level}` : level;
    result = byLowerCase.get(next.toLowerCase()) ?? next;
  }
  return result;
}

/**
 * The text is normalized matching the stored path before searching so that an existing folder that differs
 * in only case sensitivity counts as the same folder, ensuring no look alike duplicates.
 */
export function searchFolders(folders: string[], typed: string) {
  const path = matchFolderCase(folders, normalizeFolderPath(typed));
  const term = path.toLowerCase();
  const matches = term ? folders.filter(f => f.toLowerCase().includes(term)) : [];
  const existing = term ? folders.find(f => f.toLowerCase() === term) : undefined;
  return { path, matches, existing, canCreate: path !== '' && existing === undefined };
}

/**
 * On Enter pick the folder that was type, else the only match, else the typed path as a new folder
 * only when nothing else matches resulting in a partial search becoming a folder.
 */
export function folderForEnter(search: ReturnType<typeof searchFolders>): string | null {
  if (search.existing !== undefined) return search.existing;
  if (search.matches.length === 1) return search.matches[0];
  if (search.canCreate && search.matches.length === 0) return search.path;
  return null;
}

export const GLOBAL_FOLDER = 'global';
export type QueryScope = 'program' | 'global';

export function treePath(scope: QueryScope, folder: string): string {
  if (scope === 'program') return folder;
  return folder ? `${GLOBAL_FOLDER}/${folder}` : GLOBAL_FOLDER;
}

/** Where a tree path is stored, and the folder within it. The "global" level matches in any case. */
export function splitTreePath(path: string): { scope: QueryScope, folder: string } {
  const normalized = normalizeFolderPath(path);
  const [first, ...rest] = normalized.split('/');
  return first.toLowerCase() === GLOBAL_FOLDER
    ? { scope: 'global', folder: rest.join('/') }
    : { scope: 'program', folder: normalized };
}

/** global -> Global */
export function displayFolderPath(path: string): string {
  const { scope, folder } = splitTreePath(path);
  if (scope === 'program') return folder;
  return folder ? `Global/${folder}` : 'Global';
}

/** Ensures the global folder is displayed first */
export function globalFirst<T extends { path: string }>(folders: T[]): T[] {
  return folders.filter(f => f.path === GLOBAL_FOLDER).concat(folders.filter(f => f.path !== GLOBAL_FOLDER));
}

/**
 * What typed folder text means in a picker limited to one store (the Edit dialog, where queries can't change stores).
 * Locked to Global, a typed path is inside Global: 'reports' -> 'global/reports' ('Global/reports' isn't prefixed
 * twice). Locked to this program, a Global path is `blocked`. Unlocked (or empty), the text is just normalized.
 */
export function scopeTypedPath(typed: string, lockedScope?: QueryScope): { path: string, blocked: boolean } {
  const path = normalizeFolderPath(typed);
  if (!lockedScope || !path) return { path, blocked: false };
  const { scope, folder } = splitTreePath(path);
  if (lockedScope === 'global') return { path: treePath('global', scope === 'global' ? folder : path), blocked: false };
  return { path, blocked: scope === 'global' };
}

/**
 * Adds an empty Global folder to the tree's top level if nothing is saved in Global yet, so people can see it exists.
 * Leaves the tree unchanged when Global already has queries.
 */
export function withGlobalFolder<Q>(tree: FolderNode<Q>): FolderNode<Q> {
  if (tree.folders.some(f => f.path === GLOBAL_FOLDER)) return tree;
  const global: FolderNode<Q> = { name: GLOBAL_FOLDER, path: GLOBAL_FOLDER, folders: [], queries: [], count: 0 };
  return { ...tree, folders: [global, ...tree.folders] };
}
